import { api } from '../config/api';

/**
 * Web Push — registra o service worker e inscreve o dispositivo do usuário.
 * Chamado uma vez após o login (AppContext).
 */

// Converte a chave pública VAPID (base64url) para Uint8Array — exigido pelo PushManager
const urlBase64ToUint8Array = (base64: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const base64Clean = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64Clean);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
};

const suportaPush = (): boolean =>
  'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export class PushService {
  /** Navegador tem suporte a Web Push (service worker + PushManager + Notification). */
  static suporta(): boolean {
    return suportaPush();
  }

  /** Registra o SW, pede permissão e salva a subscription no backend. Silencioso em caso de erro. */
  static async inicializar(): Promise<void> {
    try {
      if (!suportaPush()) return;

      const registration = await navigator.serviceWorker.register('/sw.js');

      // Se o usuário já negou, não insiste
      if (Notification.permission === 'denied') return;

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') return;

      const resp = await api.get<{ publicKey: string }>('/push/vapid-public');
      if (!resp?.publicKey) return;
      const appServerKey = urlBase64ToUint8Array(resp.publicKey);

      let subscription = await registration.pushManager.getSubscription();

      // Se a subscription existente foi criada com outra chave VAPID (rotação), reinscreve
      if (subscription) {
        const atual = subscription.options.applicationServerKey;
        const atualBytes = atual ? new Uint8Array(atual) : null;
        const mudouChave = !atualBytes || atualBytes.length !== appServerKey.length ||
          atualBytes.some((b, i) => b !== appServerKey[i]);
        if (mudouChave) {
          await subscription.unsubscribe();
          subscription = null;
        }
      }

      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: appServerKey as BufferSource
        });
      }

      await api.post('/push/subscribe', subscription.toJSON());
    } catch (error) {
      console.log('Push não inicializado:', error);
    }
  }

  /** Remove a subscription do backend (usado em logout, se necessário). */
  static async desinscrever(): Promise<void> {
    try {
      if (!suportaPush()) return;
      const registration = await navigator.serviceWorker.getRegistration();
      const subscription = await registration?.pushManager.getSubscription();
      if (subscription) {
        await api.post('/push/unsubscribe', { endpoint: subscription.endpoint });
        await subscription.unsubscribe();
      }
    } catch (error) {
      console.log('Erro ao desinscrever push:', error);
    }
  }
}

export default PushService;
